import * as RadixSelect from '@radix-ui/react-select';
import React from 'react';
import { tv } from 'tailwind-variants';
import { CheckIcon, ChevronBottomSmallIcon } from '@marvelapp/ballpark-icons';
import { BtwButton as Button } from './BtwButton';
const select = tv({
    slots: {
        selectTrigger: [
            // Some base resets specific to the Select button
            'font-medium',
            'justify-between',
            '[&>button]:data-[state=open]:ring-1',
            '[&>button]:data-[state=open]:ring-gray-600/30',
        ],
        selectContent: [
            // Base styles for the dropdown container
            'border',
            'border-gray-600/20',
            'bg-white',
            'rounded-md',
            'shadow-lg',
            'relative',
            // Layout
            'flex',
            'min-w-[var(--radix-select-trigger-width)]',
            'max-w-[320px]',
            'max-h-[var(--radix-select-content-available-height)]',
            'h-max',
            'overflow-y-scroll',
        ],
        selectItem: [
            // Reset
            'appearance-none',
            'bg-transparent',
            'border-none',
            'cursor-pointer',
            'outline-none',
            // Layout
            'pl-3',
            'pt-2',
            'pr-2',
            'pb-2',
            'flex',
            'items-center',
            'justify-between',
            'gap-3',
            // Base styles
            'bg-white',
            'rounded-md',
            // Hover styles
            'data-[highlighted]:bg-gray-100',
            'data-[highlighted]:outline-none',
            // Typography
            'text-gray-900',
            'text-sm',
        ],
        selectSeparator: [
            // Using a pseudo-element to absolutely position the actual 1px separator
            'before:w-[calc(100%_+_20px)]',
            'before:-left-2.5',
            'before:absolute',
            'before:content-[""]',
            'before:block',
            'before:border-b',
            'before:border-gray-600/20',
            'flex-1',
            'my-1',
            'relative',
        ],
    },
});
const { selectContent, selectItem, selectTrigger, selectSeparator } = select();
function Trigger(props) {
    const { children, className, size = 'base', variant, width, testId = 'select-trigger', isTruncated, hasPlaceholder, leadingIcon, } = props;
    return (React.createElement(RadixSelect.Trigger, { "data-testid": testId, asChild: true },
        React.createElement("span", { className: "flex-1" },
            leadingIcon && (React.createElement(Button, { size: size, variant: variant, standaloneIcon: leadingIcon, className: "flex xl:hidden" })),
            React.createElement(Button, { size: size, className: `${selectTrigger({ className })} ${isTruncated ? 'min-w-0 flex-1' : ''} ${hasPlaceholder ? 'text-gray-500' : ''} ${leadingIcon ? 'hidden xl:flex' : ''}`, variant: variant, width: width, leadingIcon: leadingIcon, trailingIcon: React.createElement(RadixSelect.Icon, null,
                    React.createElement(ChevronBottomSmallIcon, { className: "h-6 w-6" })) },
                React.createElement(RadixSelect.Value, { asChild: true },
                    React.createElement("span", { className: "truncate" }, children))))));
}
function Content(props) {
    const { children, className, testId = 'select-portal' } = props;
    return (React.createElement(RadixSelect.Portal, { "data-testid": testId, className: "z-[100000]" },
        React.createElement(RadixSelect.Content, { "data-testid": "select-content", position: "popper", className: selectContent({ className }), collisionPadding: { top: 70, bottom: 20 }, sideOffset: 4 },
            React.createElement(RadixSelect.ScrollUpButton, null),
            React.createElement(RadixSelect.Viewport, { className: "flex flex-col gap-1 p-2.5" }, children),
            React.createElement(RadixSelect.ScrollDownButton, null))));
}
function Item(props) {
    const { className, children, value, testId = 'select-option', placeholder, } = props;
    return (React.createElement(RadixSelect.Item, { placeholder: placeholder, className: selectItem({ className }), value: value, "data-testid": testId },
        React.createElement(RadixSelect.ItemText, null, children),
        React.createElement(RadixSelect.ItemIndicator, null,
            React.createElement(CheckIcon, { className: "h-5 w-5" }))));
}
function Separator(props) {
    const { className } = props;
    return React.createElement(RadixSelect.Separator, { className: selectSeparator({ className }) });
}
//
// Select component
//
export const Select = {
    Container: RadixSelect.Root,
    Trigger,
    Content,
    Item,
    Separator,
};
